import React, { useState } from 'react';
import { HelpCircle, Search, BookOpen, MessageCircle, Phone, Mail, FileText, Video, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const Help = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    {
      question: "How do I add stocks to my portfolio?",
      answer: "To add stocks, go to your Dashboard and click the 'Add Stock' button. Enter the stock symbol, quantity, and purchase price. You can also import stocks from CSV files for bulk additions."
    },
    {
      question: "How is P&L calculated?",
      answer: "P&L (Profit & Loss) is calculated as: (Current Price - Purchase Price) × Quantity. For sold stocks, it shows realized P&L. For current holdings, it shows unrealized P&L based on live market prices."
    },
    {
      question: "Can I export my portfolio data?",
      answer: "Yes! You can export your portfolio data to CSV format. Go to Dashboard → Export Data. This includes all your stock holdings, transactions, and performance metrics."
    },
    {
      question: "How do I update stock prices?",
      answer: "Stock prices are automatically updated in real-time. If you need to refresh manually, click the refresh button in your portfolio view. Prices are sourced from reliable market data providers."
    },
    {
      question: "What sectors are supported?",
      answer: "We support all major sectors including IT, Banking, Energy, Pharma, FMCG, Auto, and more. Stocks are automatically categorized based on their NSE/BSE sector classification."
    },
    {
      question: "How do I reset my password?",
      answer: "Click 'Forgot Password' on the login page. Enter your email address and we'll send you a password reset link. Make sure to check your spam folder if you don't receive it."
    }
  ];

  const guides = [
    {
      title: "Getting Started Guide",
      description: "Learn the basics of portfolio management",
      icon: BookOpen,
      color: "bg-blue-500",
      link: "#"
    },
    {
      title: "Portfolio Analysis",
      description: "Understanding your investment performance",
      icon: FileText,
      color: "bg-green-500",
      link: "#"
    },
    {
      title: "Video Tutorials",
      description: "Step-by-step video guides",
      icon: Video,
      color: "bg-purple-500",
      link: "#"
    },
    {
      title: "User Manual",
      description: "Comprehensive feature documentation",
      icon: Download,
      color: "bg-orange-500",
      link: "#"
    }
  ];

  const supportChannels = [
    {
      title: "Email Support",
      description: "Get help via email",
      icon: Mail,
      contact: "info@stockkhata.com",
      color: "bg-blue-500"
    },
    {
      title: "Whatsapp Support",
      description: "Chat with our support team",
      icon: MessageCircle,
      contact: "+91-7500068589",
      color: "bg-green-500"
    },
    {
      title: "Phone Support",
      description: "Call us directly",
      icon: Phone,
      contact: "+91-7500068589",
      color: "bg-purple-500"
    }
  ];

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-6">
            <HelpCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Help & Support Center</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Find answers to your questions, learn how to use StockKhata, and get the support you need
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search for help topics, guides, or FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-3 text-lg border-2 border-gray-200 focus:border-blue-500 rounded-xl"
            />
          </div>
        </div>

        {/* Quick Guides */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Guides</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {guides.map((guide, index) => (
              <Card key={index} className="hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer">
                <CardHeader className="pb-3">
                  <div className={`w-12 h-12 ${guide.color} rounded-xl flex items-center justify-center mb-3`}>
                    <guide.icon className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-lg">{guide.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">{guide.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {filteredFaqs.map((faq, index) => (
              <Card key={index} className="border-2 hover:border-blue-200 transition-all duration-300">
                <CardHeader 
                  className="pb-3 cursor-pointer"
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg text-gray-900">{faq.question}</CardTitle>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      {expandedFaq === index ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardHeader>
                {expandedFaq === index && (
                  <CardContent className="pt-0">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>

        {/* Support Channels */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Get Support</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {supportChannels.map((channel, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-all duration-300 hover:scale-105">
                <CardHeader className="pb-3">
                  <div className={`w-16 h-16 ${channel.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                    <channel.icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl">{channel.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{channel.description}</p>
                  <p className="text-blue-600 font-semibold">{channel.contact}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Additional Help */}
        {/* <div className="text-center">
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="py-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Still Need Help?</h3>
              <p className="text-gray-600 mb-6">
                Can't find what you're looking for? Our support team is here to help you succeed.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="bg-blue-600 hover:bg-blue-700 px-6 py-3">
                  <Mail className="w-4 h-4 mr-2" />
                  Send us a message
                </Button>
                <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-3">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Start live chat
                </Button>
              </div>
            </CardContent>
          </Card>
        </div> */}
      </div>
    </div>
  );
};

export default Help;
